import { useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  Card,
  CardContent,
  ErrorPage,
  FeedbackState,
  MetricCard,
  MetricStrip,
  PageContent,
  PageShell,
  Skeleton,
} from "@h2j-data-team/ui";

import { ActivityFeed } from "@/features/dashboard/components/activity-feed";
import { ConnectionsStrip } from "@/features/dashboard/components/connections-strip";
import { Heatmap } from "@/features/dashboard/components/heatmap";
import { Hero, QuickActions } from "@/features/dashboard/components/hero";
import { PendingChangesPanel } from "@/features/dashboard/components/pending-changes-panel";
import { dashboardOverviewQueryOptions } from "@/features/dashboard/api";
import { makeJobTypeLabeler } from "@/features/jobs/labels";
import { supportCode } from "@/components/error-screen";

export const Route = createFileRoute("/_authenticated/dashboard")({
  staticData: { pageTitle: "breadcrumbs.dashboard" },
  component: DashboardPage,
});

function DashboardPage() {
  const { t } = useTranslation("dashboard");
  const { t: tCommon } = useTranslation("common");
  const {
    data: overview,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(dashboardOverviewQueryOptions);
  const jobTypeLabel = useMemo(() => makeJobTypeLabeler(t), [t]);

  if (isLoading) {
    return (
      <PageShell header={<Hero />}>
        <PageContent>
          <FeedbackState state="loading" title={t("loading")} skeleton={<DashboardSkeleton />} />
        </PageContent>
      </PageShell>
    );
  }

  // The failure's message never reaches the screen; a 5xx carries its request id
  // as the support code.
  if (isError || !overview) {
    return (
      <ErrorPage
        kind="load-failed"
        header={<Hero />}
        title={t("loadFailed")}
        description={t("loadFailedDescription")}
        supportCode={supportCode(error, tCommon("error.supportCode"))}
        actions={
          <Button variant="outline" onClick={() => void refetch()}>
            {tCommon("tryAgain")}
          </Button>
        }
      />
    );
  }

  const { kpi, heatmap, connectionsStrip } = overview;
  const lastFailureHelper =
    kpi.failedRuns7d.lastFailureDaysAgo === null
      ? t("kpis.failedRunsNone")
      : kpi.failedRuns7d.lastFailureDaysAgo === 0
        ? t("kpis.failedRunsToday")
        : t("kpis.failedRunsLast", { count: kpi.failedRuns7d.lastFailureDaysAgo });

  return (
    <PageShell header={<Hero />}>
      <PageContent>
        <div className="flex flex-col gap-6">
          <QuickActions />

          {/* KPI strip: 5 hairline-divided cells inside one card */}
          <Card className="py-[18px]">
            <CardContent>
              <MetricStrip aria-label={t("kpis.label")}>
                <MetricCard
                  label={t("kpis.connections")}
                  value={kpi.connections.total}
                  description={t("kpis.connectionsHelper", {
                    active: kpi.connections.active,
                    idle: kpi.connections.idle,
                  })}
                />

                <MetricCard
                  label={t("kpis.activeJobs")}
                  value={
                    <span className={kpi.activeJobs.count > 0 ? "text-brand" : undefined}>
                      {kpi.activeJobs.count}
                    </span>
                  }
                  description={
                    kpi.activeJobs.running.length > 0
                      ? kpi.activeJobs.running.map((j) => jobTypeLabel(j.type)).join(" · ")
                      : t("kpis.activeJobsHelper")
                  }
                />

                <MetricCard
                  label={t("kpis.appsTracked")}
                  value={kpi.appsTracked.total}
                  change={
                    kpi.appsTracked.newThisWeek > 0
                      ? {
                          value: t("kpis.deltaThisWeek", { count: kpi.appsTracked.newThisWeek }),
                          direction: "up",
                          tone: "positive",
                        }
                      : undefined
                  }
                  description={t("kpis.appsTrackedHelper")}
                />

                <MetricCard
                  label={t("kpis.pendingChanges")}
                  value={
                    <>
                      {kpi.pendingChanges.total}
                      {kpi.pendingChanges.conflicts > 0 ? (
                        <Badge variant="danger">
                          {t("kpis.conflictsBadge", { count: kpi.pendingChanges.conflicts })}
                        </Badge>
                      ) : null}
                    </>
                  }
                  description={t("kpis.pendingChangesHelper", {
                    variables: kpi.pendingChanges.variables,
                    masterItems: kpi.pendingChanges.masterItems,
                  })}
                />

                <MetricCard
                  label={t("kpis.failedRuns7d")}
                  value={kpi.failedRuns7d.count}
                  description={lastFailureHelper}
                />
              </MetricStrip>
            </CardContent>
          </Card>

          {/* Body: activity column + side panels */}
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
            <Card className="gap-0 py-[18px]">
              <CardContent>
                <ActivityFeed jobTypeLabel={jobTypeLabel} />
              </CardContent>
            </Card>

            <div className="flex flex-col gap-6">
              <Card className="gap-0 py-[18px]">
                <CardContent>
                  <PendingChangesPanel
                    total={kpi.pendingChanges.total}
                    conflicts={kpi.pendingChanges.conflicts}
                    items={kpi.pendingChanges.sampleItems}
                  />
                </CardContent>
              </Card>

              <Card className="gap-0 py-[18px]">
                <CardContent>
                  <h2 className="text-[14px] font-semibold text-ink">{t("heatmap.title")}</h2>
                  <div className="pt-3">
                    <Heatmap data={heatmap} cols={7} />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <ConnectionsStrip items={connectionsStrip} />
        </div>
      </PageContent>
    </PageShell>
  );
}

function DashboardSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <Skeleton className="h-24 w-full" />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
        <Skeleton className="h-96 w-full" />
        <div className="flex flex-col gap-6">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-44 w-full" />
        </div>
      </div>
      <Skeleton className="h-24 w-full" />
    </div>
  );
}
